import React from 'react';
import { useTranslation } from 'react-i18next';
import MultiSelectChips from '@/components/MultiSelectChips';
import { COVERAGE_COUNTRIES } from '@/components/CoverageFields';
import NIGERIA_LGAS from '@/data/nigeriaLgas.json';

const STATE_OPTIONS = Object.keys(NIGERIA_LGAS.states).map((s) => ({ value: s, label: s === 'Abuja' ? 'Abuja (FCT)' : s }));

/** Every chosen Nigerian state has all its local areas, or at least one. */
export function buyingAreaComplete({ countries, states, lgas }) {
  if (!countries.length) return false;
  if (!countries.includes('NG')) return true;
  return states.length > 0 && states.every((s) => !lgas[s] || lgas[s].length > 0);
}

/**
 * Where an offtaker buys (PR 75): one or more countries; for Nigeria one or
 * more states, and per state all its local government areas (the default)
 * or only some. value = { countries, states, lgas: { state: [] | null } } —
 * null/missing means "all", an array lists the chosen ones.
 */
export default function BuyingAreaFields({ value, onChange }) {
  const { t } = useTranslation();
  const { countries, states, lgas } = value;
  const set = (fields) => onChange({ ...value, ...fields });

  const setStates = (next) => {
    const kept = Object.fromEntries(Object.entries(lgas).filter(([s]) => next.includes(s)));
    set({ states: next, lgas: kept });
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="block text-base font-semibold text-gray-900">{t('wizard.buying.countries')}</label>
        <MultiSelectChips
          options={COVERAGE_COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
          value={countries}
          onChange={(next) => set(next.includes('NG') ? { countries: next } : { countries: next, states: [], lgas: {} })}
          placeholder={t('wizard.buying.countriesPlaceholder')}
          searchPlaceholder={t('wizard.search')}
          emptyText={t('wizard.noMatch')}
          testId="buying-countries"
        />
      </div>
      {countries.includes('NG') && (
        <div className="space-y-2">
          <label className="block text-base font-semibold text-gray-900">{t('wizard.buying.states')}</label>
          <MultiSelectChips
            options={STATE_OPTIONS}
            value={states}
            onChange={setStates}
            placeholder={t('wizard.buying.statesPlaceholder')}
            searchPlaceholder={t('wizard.search')}
            emptyText={t('wizard.noMatch')}
            testId="buying-states"
          />
        </div>
      )}
      {states.map((state) => {
        const all = !lgas[state];
        return (
          <div key={state} className="rounded-xl border bg-white p-3 space-y-2" data-testid={`buying-lgas-${state}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">{t('wizard.buying.lgasOf', { state })}</p>
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input type="checkbox" checked={all} className="w-5 h-5 accent-farm-primary"
                       onChange={(e) => set({ lgas: { ...lgas, [state]: e.target.checked ? null : [] } })}
                       data-testid={`buying-all-${state}`} />
                {t('wizard.buying.allLgas')}
              </label>
            </div>
            {!all && (
              <MultiSelectChips
                options={(NIGERIA_LGAS.states[state] || []).map((a) => ({ value: a, label: a }))}
                value={lgas[state] || []}
                onChange={(next) => set({ lgas: { ...lgas, [state]: next } })}
                placeholder={t('wizard.buying.lgasPlaceholder')}
                searchPlaceholder={t('wizard.search')}
                emptyText={t('wizard.noMatch')}
                testId={`buying-lga-pick-${state}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** For saving: "all" (null) becomes an empty list, as the API expects. */
export function buyingAreaPayload({ countries, states, lgas }) {
  return {
    countries,
    states,
    lgas: Object.fromEntries(states.map((s) => [s, lgas[s] || []])),
  };
}

/** From the saved shape (empty list = all) back to the form's. */
export function buyingAreaForm(saved) {
  const area = saved || {};
  return {
    countries: area.countries || ['NG'],
    states: area.states || [],
    lgas: Object.fromEntries(Object.entries(area.lgas || {}).filter(([, v]) => v && v.length)),
  };
}
